import React, { useState, useRef, useEffect } from 'react';
import { Mail, ChevronDown, Copy, Check } from 'lucide-react';
import { useLocale } from '../i18n/LocaleProvider';

// Robust "send email" control. A bare mailto: silently fails for users with no
// registered mail handler, so this offers explicit choices that work for
// everyone: Gmail / Outlook web compose, the OS mail app, or copy the address.

const enc = encodeURIComponent;
const gmailUrl = (to, su, body) =>
  `https://mail.google.com/mail/?view=cm&fs=1&to=${enc(to)}&su=${enc(su)}&body=${enc(body)}`;
const outlookUrl = (to, su, body) =>
  `https://outlook.office.com/mail/deeplink/compose?to=${enc(to)}&subject=${enc(su)}&body=${enc(body)}`;
const mailtoUrl = (to, su, body) => `mailto:${to}?subject=${enc(su)}&body=${enc(body)}`;

export default function MailMenu({ email, subject = '', body = '', label, className = '', block = false }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — the address is shown in the footer anyway */
    }
  };

  const item =
    'flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink transition-colors hover:bg-beige';

  return (
    <div ref={ref} className={`relative ${block ? 'w-full' : 'inline-block'}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={className}
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-xl border border-sand bg-cream py-1 text-greenDeep shadow-card"
        >
          <a role="menuitem" href={gmailUrl(email, subject, body)} target="_blank" rel="noopener noreferrer" className={item} onClick={() => setOpen(false)}>
            <Mail className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.75} /> {t.mailGmail}
          </a>
          <a role="menuitem" href={outlookUrl(email, subject, body)} target="_blank" rel="noopener noreferrer" className={item} onClick={() => setOpen(false)}>
            <Mail className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.75} /> {t.mailOutlook}
          </a>
          <a role="menuitem" href={mailtoUrl(email, subject, body)} className={item} onClick={() => setOpen(false)}>
            <Mail className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.75} /> {t.mailEmailApp}
          </a>
          <button role="menuitem" type="button" onClick={copy} className={item}>
            {copied ? (
              <Check className="h-4 w-4 shrink-0 text-green" strokeWidth={2} />
            ) : (
              <Copy className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.75} />
            )}
            {copied ? t.mailCopied : t.mailCopy}
          </button>
          <p className="select-all border-t border-sand/70 px-4 pb-2 pt-2 font-num text-[11px] text-muted-foreground">
            {email}
          </p>
        </div>
      )}
    </div>
  );
}
